import { Alignment, DOMUtil, LogManager, ObjectUtil } from "@mcleod/core";
import { Component } from "./Component";
import { Cursor } from "./Cursor";
import { ResizeManager, ResizeStorageProps } from "./ResizeManager";
import { Layout } from "../components/layout/Layout";
import { SlideoutDecorator } from "../page/decorators/SlideoutDecorator";
import { Container } from "./Container";
import { MainPageAccessor } from "./MainPageAccessor";

const log = LogManager.getLogger("components.base.SlideoutSizeManager");
const DEFAULT_MIN_WIDTH = 100;
const LOCAL_SETTINGS_KEY = "slideout.settings";

interface SlideoutSettings { width: number; }

export class SlideoutSizeManager extends ResizeManager<Component> {
    constructor(component: Component, direction: Alignment) {
        super({
            component: component,
            defaultMinWidth: DEFAULT_MIN_WIDTH,
            dragEdge: SlideoutSizeManager.determineResizeEdge(direction)
        });
    }

    private static determineResizeEdge(direction: Alignment): Alignment.RIGHT | Alignment.LEFT {
        if (direction === Alignment.LEFT)
            return Alignment.RIGHT;
        return Alignment.LEFT;
    }

    protected override createResizeDiv(): HTMLDivElement {
        const div = document.createElement('div');
        div.id = "slideoutResizeDiv";
        const resizeStyles: Partial<CSSStyleDeclaration> = {
            position: "absolute",
            top: "0",
            width: "15px",
            height: "100%",
            cursor: Cursor.EW_RESIZE,
            zIndex: "999",
            [this.dragEdge === Alignment.RIGHT ? "right" : "left"]: "0"
        };
        Object.assign(div.style, resizeStyles);

        return div;
    }

    protected override onResizeEnd(_e: MouseEvent) {
        this.component.width = DOMUtil.getElementWidthPercentage(this.component._element) + "%";
    }

    protected override get storageProps(): ResizeStorageProps {
        return {
            localSettingsKey: LOCAL_SETTINGS_KEY,
            currentUserSettings: this.currentSlideoutWidthPercentage,
            applyUserSettings: this.applySavedSlideoutWidth,
            componentSettingsKey: this.slideoutWidthSettingsKey.bind(this)
        };
    }

    private currentSlideoutWidthPercentage(): SlideoutSettings {
        const percentWidth = DOMUtil.getElementWidthPercentage(this.component._element);
        if (percentWidth == null || !this.isValidPercentage(percentWidth)) {
            log.debug("Could not determine width for slideout. Width is not a percentage.")
            return null;
        }

        return { width: percentWidth }
    }

    private applySavedSlideoutWidth(settings: SlideoutSettings) {
        if (ObjectUtil.isEmptyObject(settings))
            return;

        if (this.isValidPercentage(settings.width))
            this.component.width = `${settings.width}%`;
    }

    private isValidPercentage(width: number): boolean {
       return width > 0 && width <= 100;
    }

    private slideoutWidthSettingsKey(): string {
        if (this.component instanceof Layout)
            return this.component.layoutName;
        if (this.component instanceof SlideoutDecorator && this.component.layout)
            return this.component.layout.layoutName;
        return SlideoutSizeManager.findLayout(this.component)?.layoutName;
    }

    private static findLayout(component: Component) : Layout {
        if (component instanceof SlideoutDecorator)
            return component.layout;
        if (component instanceof Layout)
            return component;
        if (component instanceof Container)
            return this.findMainLayout(component);
    }

    // Finds the main layout in the slideout Container. If multiple layouts are found,
    // the first layout is returned if all other layouts are nested within it.
    private static findMainLayout(wrapper: Container): Layout {
        const layouts = [];

        wrapper.forEveryChildComponent(child => {
            if (child instanceof Layout && child.isNested !== false)
                layouts.push(child);
        });

        if (layouts.length === 0) return null;
        if (layouts.length === 1) return layouts[0];

        const mainLayout = layouts[0];

        for (let i = 1; i < layouts.length; i++) {
            if (!DOMUtil.isOrContains(mainLayout._element, layouts[i]._element))
                return null;
        }
        return mainLayout;
    }

    protected override get containingElement(): HTMLElement {
        return MainPageAccessor.router._element;
    }
}
